import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Send,
  MessageCircle,
  Image as ImageIcon,
  FileText,
  Mic,
  Video,
  CheckCheck,
  AlertCircle,
  RefreshCw,
  Paperclip,
  X,
  Download,
  Trash2,
  Sparkles,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { whatsappApi, projectsApi, aiApi } from "@/lib/crmApi";
import { cn } from "@/lib/utils";
import { formatDateTime, getInitials } from "@/lib/crmHelpers";

// WhatsApp's hard limits per file type. Anything bigger gets rejected by the
// platform before even reaching the recipient — fail fast in the UI.
const MAX_SIZE_MB = {
  image: 16,
  video: 64,
  audio: 16,
  document: 100,
};
const formatBytes = (n) => {
  if (n < 1024) return `${n} o`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} Ko`;
  return `${(n / 1024 / 1024).toFixed(1)} Mo`;
};
const inferMediaType = (file) => {
  if (!file?.type) return "document";
  if (file.type.startsWith("image/")) return "image";
  if (file.type.startsWith("video/")) return "video";
  if (file.type.startsWith("audio/")) return "audio";
  return "document";
};

const DEFAULT_QUICK_TEMPLATES = [
  { label: "👋 Premier contact", body: (lead) => `Bonjour ${firstName(lead)}, je suis commercial chez SOGI SARL. Suite à votre intérêt pour nos programmes immobiliers, quel jour seriez-vous disponible pour un échange rapide ?` },
  { label: "📁 Catalogue", body: (lead) => `Bonjour ${firstName(lead)}, comme convenu je vous transmets notre catalogue${lead.programme_immobilier ? ` du programme ${lead.programme_immobilier}` : ""}. Je reste à votre disposition.` },
  { label: "📅 Visite", body: (lead) => `Bonjour ${firstName(lead)}, je vous propose une visite ce samedi matin pour découvrir le programme. Cela vous conviendrait-il ?` },
  { label: "💬 Relance douce", body: (lead) => `Bonjour ${firstName(lead)}, avez-vous eu le temps de regarder les informations transmises ? Je reste à votre écoute.` },
];

const firstName = (lead) => (lead?.nom || "").split(/\s+/)[0] || "";

/**
 * v1.8.0 — Per-project quick templates. When the project defines its own
 * `quick_templates`, they REPLACE the defaults (not merge). Empty / missing
 * → fall back to DEFAULT_QUICK_TEMPLATES. Body is a string with {nom} and
 * {prenom} placeholders which we substitute at render time.
 */
function substituteTemplate(body, lead) {
  return String(body || "")
    .replaceAll("{nom}", lead?.nom || "")
    .replaceAll("{prenom}", firstName(lead));
}

function resolveTemplates(projet, lead) {
  const custom = projet?.quick_templates;
  if (Array.isArray(custom) && custom.length > 0) {
    return custom
      .filter((t) => t?.label && t?.body)
      .map((t) => ({
        label: t.label,
        body: () => substituteTemplate(t.body, lead),
      }));
  }
  return DEFAULT_QUICK_TEMPLATES;
}

export default function WhatsAppConversation({ lead, onMessageSent }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [serviceStatus, setServiceStatus] = useState(null);
  const [error, setError] = useState(null);
  // Selected attachment + a transient object URL for the preview.
  // We revoke the URL on cleanup to avoid memory leaks (large videos).
  const [attached, setAttached] = useState(null); // {file, type, previewUrl}
  // Confirmation dialog for "delete entire conversation"
  const [deleteConvoOpen, setDeleteConvoOpen] = useState(false);
  const [deletingConvo, setDeletingConvo] = useState(false);
  // v1.8.0 — cache the lead's project document so we can surface its
  // per-project quick templates in the composer. Null = not loaded yet or
  // no project linked; we fall back to DEFAULT_QUICK_TEMPLATES in render.
  const [projet, setProjet] = useState(null);
  // v2.5.0 — IA composer state. `suggestions` is the N variants Claude
  // returned ; clicking one fills the draft. `loadingSuggestions` toggles a
  // spinner on the "Suggérer une réponse" button. The panel auto-hides when
  // the user picks one, sends, or clears the textarea — see clearSuggestions.
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);

  // Fetch the lead's project once per projet_id change. Cheap (one HTTP
  // request, no real-time updates needed — templates change rarely).
  useEffect(() => {
    let cancelled = false;
    if (!lead?.projet_id) {
      setProjet(null);
      return;
    }
    projectsApi
      .get(lead.projet_id)
      .then((p) => {
        if (!cancelled) setProjet(p);
      })
      .catch(() => {
        if (!cancelled) setProjet(null);
      });
    return () => {
      cancelled = true;
    };
  }, [lead?.projet_id]);

  const load = useCallback(async ({ background = false } = {}) => {
    if (!lead?.id && !lead?.telephone) return;
    // Background polls don't flash the loading spinner or clear an existing
    // error banner (so the user has time to read transient errors instead of
    // them disappearing every 8s).
    if (!background) {
      setLoading(true);
      setError(null);
    }
    try {
      // Query by lead_id when available, fall back to phone (for INCONNU
      // conversations where the WhatsApp number isn't linked to any lead).
      // Limit of 200 leaves comfortable headroom over the "50 derniers
      // messages" minimum the user expects per number — Baileys' history
      // sync pulls ~50/conversation, live messages stack on top, this
      // window captures everything we have on file.
      const messagesQuery = lead.id
        ? { leadId: lead.id, limit: 200 }
        : { phone: lead.telephone, limit: 200 };
      const markReadQuery = lead.id
        ? { lead_id: lead.id }
        : { phone: lead.telephone };
      // Query the WhatsApp session that should handle this lead's traffic.
      // When the lead belongs to a project with its own paired number we
      // probe that session — otherwise the global status would return the
      // default session, which may legitimately be disconnected while the
      // project session is online (and vice-versa). Falls back to default
      // when no project session is configured.
      const leadSessionId = lead.projet_id || null;
      const [msgs, status] = await Promise.all([
        whatsappApi.messages(messagesQuery).catch(() => ({ items: [] })),
        // Don't crash the whole conversation panel just because the status
        // probe failed (sidecar restart, transient network blip). The UI
        // already has a "Réessayer" path on the send button.
        whatsappApi
          .status(leadSessionId)
          .catch(() =>
            // 404 from a project session = "not started yet" — fall back to
            // the global session so we still get a meaningful status.
            leadSessionId
              ? whatsappApi.status().catch(() => null)
              : null
          ),
      ]);
      // Merge server response with locally-tracked messages so an optimistic
      // append (from send/sendMedia) that hasn't yet been persisted to the
      // server isn't wiped by the next poll tick. We dedupe on wa_id; any
      // local-only message (wa_id absent or temp) is kept until the server
      // returns it.
      const serverItems = msgs.items || [];
      setMessages((prev) => {
        const serverIds = new Set(
          serverItems.map((m) => m.wa_id).filter(Boolean)
        );
        const localOnly = prev.filter(
          (m) => !m.wa_id || !serverIds.has(m.wa_id)
        );
        // If the server already has all our local messages and the lengths
        // match, return prev to keep the same reference and skip re-render.
        if (
          localOnly.length === 0 &&
          serverItems.length === prev.length &&
          serverItems.every((m, i) => prev[i]?.wa_id === m.wa_id)
        ) {
          return prev;
        }
        return [...serverItems, ...localOnly];
      });
      setServiceStatus(status);
      try { await whatsappApi.markRead(markReadQuery); } catch {}
    } catch (e) {
      // Only surface errors from foreground loads — background poll failures
      // are silent (we'll retry in 8s anyway).
      if (!background) {
        setError(e?.response?.data?.detail?.message || e?.humanMessage || "Service WhatsApp indisponible");
      }
    } finally {
      if (!background) setLoading(false);
    }
  }, [lead?.id, lead?.telephone]);

  useEffect(() => {
    load();
    // Poll for new messages in BACKGROUND mode so we don't flash a spinner
    // or wipe a sticky error banner every 8 seconds.
    const id = setInterval(() => load({ background: true }), 8000);
    return () => clearInterval(id);
  }, [load]);

  // Auto-scroll on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length]);

  // Centralised error toast — used by both /send and /send-media flows so
  // they react identically to wa_not_connected, daily_cap_reached, etc.
  const reportSendError = (e) => {
    const detail = e?.response?.data?.detail;
    const code = detail?.error;
    if (code === "wa_not_connected") {
      toast.error("WhatsApp non connecté · Allez dans Paramètres → WhatsApp");
    } else if (code === "daily_cap_reached") {
      toast.error(`Quota quotidien atteint (${detail.cap} messages)`);
    } else if (code === "number_not_on_whatsapp") {
      toast.error("Ce numéro n'est pas sur WhatsApp");
    } else if (code === "file_too_large") {
      toast.error(`Fichier trop volumineux (max ${detail.max_mb} Mo)`);
    } else if (code === "invalid_media_type") {
      toast.error("Type de média non supporté");
    } else {
      toast.error(e?.humanMessage || e?.message || "Erreur d'envoi");
    }
  };

  const send = async () => {
    if (!lead?.telephone) {
      toast.error("Ce lead n'a pas de numéro de téléphone");
      return;
    }
    // Branch on whether the user attached a file: the textarea becomes the
    // caption in that case, even when empty.
    if (attached) {
      setSending(true);
      try {
        const r = await whatsappApi.sendMedia({
          phone: lead.telephone,
          leadId: lead.id,
          file: attached.file,
          mediaType: attached.type,
          caption: draft.trim(),
        });
        if (r.doc) setMessages((prev) => [...prev, r.doc]);
        setDraft("");
        clearAttachment();
        onMessageSent?.();
        toast.success("Pièce jointe envoyée");
      } catch (e) {
        reportSendError(e);
      } finally {
        setSending(false);
      }
      return;
    }

    const text = draft.trim();
    if (!text) return;
    setSending(true);
    try {
      const r = await whatsappApi.send({
        phone: lead.telephone,
        text,
        lead_id: lead.id,
      });
      // Optimistic local append
      if (r.doc) setMessages((prev) => [...prev, r.doc]);
      setDraft("");
      onMessageSent?.();
      toast.success("Message envoyé");
    } catch (e) {
      reportSendError(e);
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    // Cmd/Ctrl+Enter to send
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      send();
    }
  };

  const applyTemplate = (tpl) => {
    setDraft(tpl.body(lead));
    setSuggestions([]);
  };

  // v2.5.0 — Demande à Claude N variantes de réponse à partir du transcript
  // complet du lead. Le backend retourne `{suggestions: [...]}` (jamais
  // d'erreur HTTP, même si l'API key est down — il renvoie []). Si la liste
  // est vide on toast un message neutre plutôt que de laisser le bouton
  // tourner en vain.
  const handleSuggest = async () => {
    if (!lead?.id || loadingSuggestions) return;
    setLoadingSuggestions(true);
    try {
      const data = await aiApi.suggestReply(lead.id, 3);
      const items = Array.isArray(data?.suggestions) ? data.suggestions : [];
      if (items.length === 0) {
        toast.info("Aucune suggestion disponible pour ce lead.");
        setSuggestions([]);
      } else {
        setSuggestions(items);
      }
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Impossible de générer des suggestions."
      );
      setSuggestions([]);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const applySuggestion = (s) => {
    setDraft(s);
    setSuggestions([]);
  };

  const onFilePicked = (e) => {
    const file = e.target.files?.[0];
    // Reset the input so the same file can be re-picked after clearing
    if (e.target) e.target.value = "";
    if (!file) return;
    const type = inferMediaType(file);
    const limitMb = MAX_SIZE_MB[type];
    if (file.size > limitMb * 1024 * 1024) {
      toast.error(`Fichier trop volumineux (max ${limitMb} Mo pour ${type})`);
      return;
    }
    // Object URLs let us preview without re-reading the file. Free the
    // previous one if any.
    if (attached?.previewUrl) URL.revokeObjectURL(attached.previewUrl);
    const previewUrl = type === "image" || type === "video" ? URL.createObjectURL(file) : null;
    setAttached({ file, type, previewUrl });
  };

  const clearAttachment = () => {
    if (attached?.previewUrl) URL.revokeObjectURL(attached.previewUrl);
    setAttached(null);
  };

  // Free the object URL when the component unmounts mid-attachment
  useEffect(() => () => {
    if (attached?.previewUrl) URL.revokeObjectURL(attached.previewUrl);
  }, [attached]);

  if (error) {
    return (
      <div className="rounded-xl border border-amber-300 bg-amber-50 p-5 text-sm dark:border-amber-900/40 dark:bg-amber-950/40">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 flex-none text-amber-600" />
          <div>
            <p className="font-semibold">WhatsApp indisponible pour le moment</p>
            <p className="mt-1 text-xs text-muted-foreground">{error}</p>
            <p className="mt-2 text-xs">
              Allez dans <strong>Paramètres → WhatsApp</strong> pour vérifier la connexion.
            </p>
            <Button onClick={load} size="sm" variant="outline" className="mt-3 gap-1.5">
              <RefreshCw className="h-3.5 w-3.5" />
              Réessayer
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Accept several "ready" synonyms — Baileys can report "connected", "ready"
  // or "open" depending on the sidecar version. Also fall back to truthy
  // boolean fields.
  //
  // Three-state logic:
  //   - serviceStatus === null    → not yet probed → "loading" (no pill)
  //   - status looks connected    → green "Connecté" pill
  //   - anything else             → amber warning
  //
  // Previously we returned `true` while loading which caused the inbox to
  // briefly flash "Connecté" then immediately flip to "Service non connecté"
  // once the real status arrived. The null sentinel keeps the pill hidden
  // until we have actual data.
  const connectionLoading = serviceStatus === null && loading;
  const isConnected = serviceStatus
    ? (["connected", "ready", "open"].includes(serviceStatus?.status) ||
        Boolean(serviceStatus?.connected) ||
        Boolean(serviceStatus?.isReady))
    : false;

  // Pretty-print the lead's phone — special-cases Senegalese numbers
  // (+221 + 9 digits) so the recipient is unambiguous in the header.
  // 13+ digits = WhatsApp privacy LID (not a real phone) — we still
  // display the raw digits (the user wants SOMETHING to read), with a
  // small "LID" badge alongside to indicate it's not dial-able.
  const phoneDigits = String(lead?.telephone || "").replace(/[^0-9]/g, "");
  const isLidPhone = phoneDigits.length >= 13;
  const formatLeadPhone = (raw) => {
    if (!raw) return "";
    const d = String(raw).replace(/[^0-9]/g, "");
    if (!d) return raw;
    if (d.length === 12 && d.startsWith("221")) {
      return `+221 ${d.slice(3, 5)} ${d.slice(5, 8)} ${d.slice(8, 10)} ${d.slice(10, 12)}`;
    }
    if (d.length === 9) {
      return `+221 ${d.slice(0, 2)} ${d.slice(2, 5)} ${d.slice(5, 7)} ${d.slice(7, 9)}`;
    }
    // LIDs (13+ digits) are shown as-is — they're not phone numbers, but
    // they identify the conversation, which the user wants visible.
    if (d.length >= 13) return d;
    return `+${d}`;
  };
  const displayedPhone = formatLeadPhone(lead?.telephone);

  return (
    <div className="overflow-hidden rounded-2xl border border-border/60 bg-card shadow-soft" data-testid="wa-conversation">
      {/* Header — name + phone of the recipient on the left, refresh on the
          right. The phone was missing before so the user couldn't tell which
          number they were chatting with when several leads shared a name. */}
      <div className="flex items-center justify-between border-b border-border/60 bg-emerald-50/50 px-4 py-3 dark:bg-emerald-950/20">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-emerald-500 text-white text-xs font-bold">
            {getInitials(lead?.nom || "?")}
          </div>
          <div className="min-w-0">
            <div className="font-heading truncate text-sm font-bold" data-testid="wa-conversation-name">
              {lead?.nom || "Conversation WhatsApp"}
            </div>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground">
              {displayedPhone && (
                <span
                  className="inline-flex items-center gap-1"
                  data-testid="wa-conversation-phone"
                >
                  {/* font-mono is monospaced but tabular-nums is the explicit
                      contract: digits never reflow as the phone updates (e.g.
                      lead edited in another tab). */}
                  <span className="font-mono tabular-nums text-foreground/80">
                    {displayedPhone}
                  </span>
                  {isLidPhone && (
                    <span
                      className="rounded bg-amber-100 px-1 py-px text-[9px] font-semibold uppercase tracking-wide text-amber-700 dark:bg-amber-950/40 dark:text-amber-400"
                      title="Identifiant WhatsApp privé — pas un numéro téléphonique"
                    >
                      LID
                    </span>
                  )}
                </span>
              )}
              {connectionLoading ? (
                <span className="inline-flex items-center gap-1 text-muted-foreground">
                  <span className="h-1.5 w-1.5 rounded-full bg-zinc-400 animate-pulse" />
                  …
                </span>
              ) : isConnected ? (
                <span className="inline-flex items-center gap-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  Connecté
                </span>
              ) : (
                <span className="text-amber-600">Service non connecté · Paramètres → WhatsApp</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-none items-center gap-1">
          {/* Delete entire conversation — opens a confirmation dialog with
              the count of messages to remove and the mode (local vs revoke
              all outgoing). Disabled while loading the thread. */}
          <Button
            onClick={() => setDeleteConvoOpen(true)}
            size="icon"
            variant="ghost"
            disabled={loading || messages.length === 0}
            className="relative h-7 w-7 transition-transform active:scale-[0.96] hover:text-red-500 before:absolute before:inset-[-6px] before:content-['']"
            title="Supprimer la conversation"
            data-testid="wa-delete-conversation-btn"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
          {/* Refresh button: scale-on-press for tactile feedback, plus a
              pseudo-element extends the hit area to ~40×40px (visible button
              stays at 28×28px to keep the header compact). */}
          <Button
            onClick={load}
            size="icon"
            variant="ghost"
            className="relative h-7 w-7 transition-transform active:scale-[0.96] before:absolute before:inset-[-6px] before:content-['']"
            title="Rafraîchir"
          >
            <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
          </Button>
        </div>
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        className="scrollbar-thin h-[420px] overflow-y-auto bg-[hsl(var(--muted))]/20 px-4 py-3"
        data-testid="wa-messages-list"
      >
        {loading && messages.length === 0 ? (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
            Chargement…
          </div>
        ) : messages.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
            <MessageCircle className="h-10 w-10 text-muted-foreground/30" />
            <p className="text-sm font-semibold">Aucun message échangé</p>
            <p className="text-xs text-muted-foreground">
              Envoyez le premier message ci-dessous, ou attendez que {firstName(lead)} vous écrive.
            </p>
          </div>
        ) : (
          <div className="space-y-1.5">
            {messages.map((m, i) => (
              <Bubble
                key={m.wa_id || `${m.timestamp}-${i}`}
                m={m}
                lead={lead}
                onDelete={async (waId, mode) => {
                  // Optimistic UI: drop the bubble immediately. If the API
                  // call fails we re-fetch to restore.
                  setMessages((prev) => prev.filter((x) => x.wa_id !== waId));
                  try {
                    const r = await whatsappApi.deleteMessage(waId, { mode });
                    if (r?.revoke_error) {
                      toast.warning(
                        "Message supprimé du CRM, mais WhatsApp a refusé la révocation (trop ancien ?). Le destinataire le voit toujours."
                      );
                    } else if (mode === "for_everyone") {
                      toast.success("Message supprimé pour tout le monde");
                    } else {
                      toast.success("Message supprimé du CRM");
                    }
                  } catch (e) {
                    toast.error(
                      e?.response?.data?.detail?.message ||
                      e?.response?.data?.detail ||
                      "Échec de la suppression"
                    );
                    // Refetch to restore the bubble we removed optimistically
                    load();
                  }
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* v2.5.0 — IA suggestion panel. Affiché juste au-dessus du composer
          quand Claude a retourné des variantes. Le commercial clique sur une
          carte pour pré-remplir la textarea, puis édite à sa guise avant
          d'envoyer. Cartes empilées en colonne (2-3 phrases chacune, on veut
          que le texte soit entièrement lisible sans scroll horizontal). */}
      {suggestions.length > 0 && (
        <div className="border-t border-violet-200/70 bg-violet-50/60 px-3 py-2 dark:border-violet-900/40 dark:bg-violet-950/30">
          <div className="mb-1.5 flex items-center justify-between">
            <span className="flex items-center gap-1.5 text-[11px] font-medium text-violet-900 dark:text-violet-200">
              <Sparkles className="h-3 w-3" />
              Suggestions IA — cliquez pour remplir
            </span>
            <button
              onClick={() => setSuggestions([])}
              className="rounded p-0.5 text-violet-700 hover:bg-violet-200/60 dark:text-violet-300 dark:hover:bg-violet-900/40"
              title="Fermer les suggestions"
              data-testid="wa-suggestions-close"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
          <div className="flex flex-col gap-1.5">
            {suggestions.map((s, i) => (
              <button
                key={i}
                onClick={() => applySuggestion(s)}
                className="rounded-md border border-violet-200 bg-white px-2.5 py-1.5 text-left text-[12px] leading-snug text-foreground transition hover:border-violet-400 hover:bg-violet-50 dark:border-violet-800/50 dark:bg-background dark:hover:bg-violet-950/40"
                data-testid={`wa-suggestion-${i}`}
              >
                <span className="mr-1.5 inline-flex h-4 w-4 items-center justify-center rounded-full bg-violet-100 text-[10px] font-bold text-violet-700 dark:bg-violet-900/60 dark:text-violet-200">
                  {i + 1}
                </span>
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Templates — per-project when configured, else hardcoded defaults.
          Each render computes the list cheaply from cached `projet` state.
          v2.5.0 — le bouton "Suggérer une réponse" est épinglé en tête de
          liste : usage quotidien le plus fréquent → toujours accessible
          sans scroll, même quand le projet définit beaucoup de templates. */}
      <div className="scrollbar-thin flex items-center gap-1.5 overflow-x-auto border-t border-border/60 bg-card px-3 py-2">
        <button
          onClick={handleSuggest}
          disabled={!isConnected || loadingSuggestions || !lead?.id}
          data-testid="wa-suggest-btn"
          className="flex-none inline-flex items-center gap-1 rounded-full border border-violet-300 bg-violet-50 px-2.5 py-1 text-[11px] font-medium text-violet-800 hover:bg-violet-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-violet-800/60 dark:bg-violet-950/40 dark:text-violet-200 dark:hover:bg-violet-900/40"
          title="Demander à l'IA 3 variantes de réponse basées sur la conversation"
        >
          {loadingSuggestions ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Sparkles className="h-3 w-3" />
          )}
          {loadingSuggestions ? "Génération…" : "Suggérer une réponse"}
        </button>
        <span className="mx-0.5 h-4 w-px flex-none bg-border/80" aria-hidden />
        {resolveTemplates(projet, lead).map((t) => (
          <button
            key={t.label}
            onClick={() => applyTemplate(t)}
            data-testid={`wa-template-${t.label}`}
            className="flex-none rounded-full border border-border bg-background px-2.5 py-1 text-[11px] font-medium hover:bg-muted"
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Composer */}
      <div className="border-t border-border/60 bg-card p-3">
        {/* Hidden file input — always rendered so we can trigger it from the
            paperclip button. Accept everything WhatsApp supports. */}
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept="image/*,video/*,audio/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/plain,text/csv,application/zip,application/x-zip-compressed"
          onChange={onFilePicked}
          data-testid="wa-file-input"
        />

        {attached && (
          <AttachmentPreview attached={attached} onRemove={clearAttachment} />
        )}

        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKey}
          placeholder={
            !isConnected
              ? "Service WhatsApp non connecté"
              : attached
              ? "Ajouter une légende (optionnel) · Ctrl+Entrée pour envoyer"
              : "Tapez un message · Ctrl+Entrée pour envoyer"
          }
          rows={2}
          disabled={!isConnected || sending}
          className="resize-none text-sm"
          data-testid="wa-composer"
        />
        <div className="mt-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="h-8 w-8"
              disabled={!isConnected || sending}
              onClick={() => fileInputRef.current?.click()}
              title="Joindre une photo, vidéo ou document"
              data-testid="wa-attach-btn"
            >
              <Paperclip className="h-4 w-4" />
            </Button>
            <p className="text-[10px] text-muted-foreground">
              {attached ? `${attached.type} · ${formatBytes(attached.file.size)}` : `${draft.length} car.`}
              {" · "}
              {serviceStatus?.daily_count ?? 0}/{serviceStatus?.daily_cap ?? 200} envoyés
            </p>
          </div>
          <Button
            onClick={send}
            disabled={!isConnected || sending || (!attached && !draft.trim())}
            size="sm"
            className="gap-1.5 bg-emerald-600 text-white hover:bg-emerald-700"
            data-testid="wa-send-btn"
          >
            <Send className="h-3.5 w-3.5" />
            {sending ? "Envoi…" : "Envoyer"}
          </Button>
        </div>
      </div>

      {/* Conversation-level delete confirmation. Shows the count, the
          difference between local/for-everyone, and disables the action
          while in flight. */}
      <AlertDialog
        open={deleteConvoOpen}
        onOpenChange={(o) => !deletingConvo && setDeleteConvoOpen(o)}
      >
        <AlertDialogContent className="sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Supprimer toute la conversation ?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Tous les messages de cette conversation
              {lead?.nom ? (
                <> avec <b>{lead.nom}</b></>
              ) : null}{" "}
              vont être supprimés du CRM.
            </AlertDialogDescription>
          </AlertDialogHeader>

          {/* Sibling of the description, not nested — `AlertDialogDescription`
              renders a <p> and stuffing a styled block inside breaks the
              dialog grid layout (the amber box overflowed the dialog). */}
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-200">
            💡 <b>Supprimer pour tout le monde</b> tente d'effacer aussi côté
            lead via WhatsApp (limite ~1h par message, uniquement les envois).
            Les messages reçus restent visibles côté lead dans tous les cas.
          </div>

          <AlertDialogFooter className="flex-col gap-2 sm:flex-row">
            <AlertDialogCancel disabled={deletingConvo} className="mt-0">
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={deletingConvo}
              onClick={async (e) => {
                // We're inside a Radix dialog action — prevent the auto-close
                // until the API call finishes so the spinner stays visible.
                e.preventDefault();
                setDeletingConvo(true);
                try {
                  const r = await whatsappApi.deleteConversation({
                    leadId: lead?.id,
                    // Always pass phone too — the backend filter is OR'd
                    // so we catch messages tagged under phone_pn / LID
                    // variants that may not share the same lead_id.
                    phone: lead?.telephone,
                    mode: "local",
                  });
                  toast.success(
                    `${r.deleted || 0} message(s) supprimé(s) du CRM`
                  );
                  setMessages([]);
                  setDeleteConvoOpen(false);
                } catch (err) {
                  toast.error(
                    err?.response?.data?.detail?.message ||
                    err?.response?.data?.detail ||
                    "Échec de la suppression"
                  );
                } finally {
                  setDeletingConvo(false);
                }
              }}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="wa-delete-convo-local"
            >
              <Trash2 className="mr-1.5 h-3.5 w-3.5" />
              Supprimer du CRM
            </AlertDialogAction>
            <AlertDialogAction
              disabled={deletingConvo}
              onClick={async (e) => {
                e.preventDefault();
                setDeletingConvo(true);
                try {
                  const r = await whatsappApi.deleteConversation({
                    leadId: lead?.id,
                    // Always pass phone too — the backend filter is OR'd
                    // so we catch messages tagged under phone_pn / LID
                    // variants that may not share the same lead_id.
                    phone: lead?.telephone,
                    mode: "for_everyone",
                  });
                  const msg =
                    r.revoke_errors > 0
                      ? `${r.deleted} supprimé(s) du CRM, ${r.revoked} révoqués sur WhatsApp (${r.revoke_errors} trop anciens)`
                      : `${r.deleted} message(s) supprimé(s) ${
                          r.revoked > 0 ? `(${r.revoked} révoqués sur WhatsApp)` : ""
                        }`;
                  if (r.revoke_errors > 0) {
                    toast.warning(msg);
                  } else {
                    toast.success(msg);
                  }
                  setMessages([]);
                  setDeleteConvoOpen(false);
                } catch (err) {
                  toast.error(
                    err?.response?.data?.detail?.message ||
                    err?.response?.data?.detail ||
                    "Échec de la suppression"
                  );
                } finally {
                  setDeletingConvo(false);
                }
              }}
              className="bg-red-600 hover:bg-red-700"
              data-testid="wa-delete-convo-revoke"
            >
              <Trash2 className="mr-1.5 h-3.5 w-3.5" />
              Supprimer pour tout le monde
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* ------------------------------------------------------------------ */

/**
 * Inline preview for the file the user just picked. Shows:
 *  - actual <img>/<video> for image/video (object URL — no roundtrip)
 *  - icon + filename + size for audio/document
 * Plus a remove (X) button so the user can swap files before sending.
 */
function AttachmentPreview({ attached, onRemove }) {
  const { file, type, previewUrl } = attached;
  return (
    <div className="mb-2 flex items-start gap-3 rounded-lg border border-border/60 bg-muted/30 p-2">
      {type === "image" && previewUrl ? (
        <img src={previewUrl} alt={file.name} className="h-16 w-16 flex-none rounded object-cover" />
      ) : type === "video" && previewUrl ? (
        <video src={previewUrl} className="h-16 w-16 flex-none rounded object-cover">
          <track kind="captions" />
        </video>
      ) : (
        <div className="flex h-16 w-16 flex-none items-center justify-center rounded bg-muted text-muted-foreground">
          {type === "audio" ? <Mic className="h-6 w-6" /> : <FileText className="h-6 w-6" />}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{file.name}</p>
        <p className="text-[11px] text-muted-foreground">
          {type} · {formatBytes(file.size)}
        </p>
      </div>
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className="h-7 w-7"
        onClick={onRemove}
        title="Retirer la pièce jointe"
        data-testid="wa-attach-remove"
      >
        <X className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

function Bubble({ m, lead, onDelete }) {
  const isOut = m.direction === "out";
  const [confirmOpen, setConfirmOpen] = useState(false);
  // "for_everyone" only available for outgoing messages within ~1 hour of
  // sending (WhatsApp limitation). We don't enforce the time check here —
  // the sidecar handles it and surfaces the error if WA refuses.
  const canRevoke = isOut;
  const Icon = m.media_type === "image" ? ImageIcon
    : m.media_type === "video" ? Video
    : (m.media_type === "audio" || m.media_type === "voice") ? Mic
    : m.media_type === "document" ? FileText
    : null;
  // Render an inline preview for both outbound media (always persisted by
  // /send-media) AND inbound media (downloaded server-side in onIncomingMessage).
  // The /media/:wa_id endpoint returns 404 if the file isn't on disk for any
  // reason, so the <img>/<video>/<a> tag will simply fail to load — UX-wise
  // this gracefully degrades to "no preview".
  const hasInlinePreview = !!(m.wa_id && m.media_type && m.media_type !== 'sticker');
  const mediaSrc = hasInlinePreview ? whatsappApi.mediaUrl(m.wa_id) : null;

  // Trash button rendered on the side OPPOSITE the bubble so it doesn't
  // overlap with the avatar/name. Only visible on hover (group/bubble) and
  // requires a wa_id (synthetic history-append messages have no id yet).
  const trashBtn = m.wa_id && onDelete ? (
    <button
      type="button"
      onClick={() => setConfirmOpen(true)}
      className="opacity-0 transition-opacity group-hover/bubble:opacity-100 rounded p-1 text-muted-foreground hover:bg-muted hover:text-red-500 active:scale-[0.96]"
      title="Supprimer ce message"
      data-testid={`wa-msg-delete-${m.wa_id}`}
    >
      <Trash2 className="h-3.5 w-3.5" />
    </button>
  ) : null;

  return (
    <div
      className={cn(
        "group/bubble relative flex w-full items-end gap-1",
        isOut ? "justify-end" : "justify-start"
      )}
    >
      {/* Trash on the LEFT of an outgoing bubble (which sits on the right) */}
      {isOut && trashBtn}
      <div className={cn(
        "max-w-[80%] rounded-xl px-3 py-2 text-sm shadow-soft",
        isOut
          ? "rounded-br-sm bg-emerald-500 text-white"
          : "rounded-bl-sm bg-card border border-border"
      )}>
        {!isOut && m.pushname && (
          <div className="mb-0.5 text-[10px] font-semibold opacity-70">
            {m.pushname || getInitials(lead?.nom)}
          </div>
        )}
        {hasInlinePreview && m.media_type === "image" && (
          <img
            src={mediaSrc}
            alt={m.media_filename || "image"}
            className="mb-1 max-h-64 max-w-full rounded object-contain"
            loading="lazy"
          />
        )}
        {hasInlinePreview && m.media_type === "video" && (
          <video
            src={mediaSrc}
            className="mb-1 max-h-64 max-w-full rounded"
            controls
            preload="metadata"
          >
            <track kind="captions" />
          </video>
        )}
        {hasInlinePreview && (m.media_type === "audio" || m.media_type === "voice") && (
          <audio src={mediaSrc} controls className="mb-1 w-full" />
        )}
        {hasInlinePreview && m.media_type === "document" && (
          <a
            href={mediaSrc}
            target="_blank"
            rel="noreferrer"
            className={cn(
              "mb-1 flex items-center gap-2 rounded px-2 py-1.5 text-xs font-medium",
              isOut ? "bg-white/15 hover:bg-white/25" : "bg-muted hover:bg-muted/80"
            )}
          >
            <FileText className="h-3.5 w-3.5 flex-none" />
            <span className="truncate">{m.media_filename || "document"}</span>
            <Download className="ml-auto h-3 w-3 flex-none opacity-70" />
          </a>
        )}
        {Icon && !hasInlinePreview && (
          <div className="mb-1 inline-flex items-center gap-1 rounded bg-black/10 px-1.5 py-0.5 text-[10px] font-medium">
            <Icon className="h-3 w-3" />
            {m.media_type}
          </div>
        )}
        {m.body && m.body !== `[${m.media_type}]` && (
          <div className="whitespace-pre-wrap break-words leading-relaxed">{m.body}</div>
        )}
        <div className={cn(
          "mt-0.5 flex items-center justify-end gap-1 text-[10px]",
          isOut ? "text-white/70" : "text-muted-foreground"
        )}>
          {formatDateTime(m.timestamp)}
          {isOut && <CheckCheck className="h-3 w-3" />}
        </div>
      </div>
      {/* Trash on the RIGHT of an incoming bubble (which sits on the left) */}
      {!isOut && trashBtn}

      {/* Confirmation dialog — different copy for outgoing (revoke option
          available) vs incoming (local-only). */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce message ?</AlertDialogTitle>
            <AlertDialogDescription>
              Aperçu :{" "}
              <span className="italic text-foreground/80">
                « {(m.body || `[${m.media_type || "média"}]`).slice(0, 120)}
                {(m.body || "").length > 120 ? "…" : ""} »
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          {/* Sibling note (avoids the asChild/<p> overflow bug). */}
          <p className="text-sm text-muted-foreground">
            {canRevoke ? (
              <>
                Choisis ce que tu veux faire. La <b>révocation WhatsApp</b>{" "}
                ne marche que pour les messages envoyés il y a moins de ~1 heure.
              </>
            ) : (
              <>
                Ce message a été <b>reçu du lead</b>, on ne peut pas le supprimer
                côté WhatsApp. La suppression sera locale (visible côté CRM
                uniquement).
              </>
            )}
          </p>
          <AlertDialogFooter className="flex-col gap-2 sm:flex-row">
            <AlertDialogCancel className="mt-0">Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                onDelete?.(m.wa_id, "local");
              }}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid={`wa-msg-delete-local-${m.wa_id}`}
            >
              <Trash2 className="mr-1.5 h-3.5 w-3.5" />
              Supprimer du CRM
            </AlertDialogAction>
            {canRevoke && (
              <AlertDialogAction
                onClick={() => {
                  onDelete?.(m.wa_id, "for_everyone");
                }}
                className="bg-red-600 hover:bg-red-700"
                data-testid={`wa-msg-delete-revoke-${m.wa_id}`}
              >
                <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                Supprimer pour tout le monde
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
